import React, { useState } from 'react';
import { ArrowLeft, Star, MapPin, Clock, Phone, Heart, Copy, Gift, Utensils, Award } from 'lucide-react';
import { Store } from '../../types';
import { Button } from '../ui/Button';
import { LoyaltyCard } from '../LoyaltyCard';
import { useToast } from '../ToastContext';
interface StoreProfileScreenProps {
  store: Store;
  onBack: () => void;
  onViewMenu: () => void;
  onCheckIn: () => void;
}
export function StoreProfileScreen({
  store,
  onBack,
  onViewMenu,
  onCheckIn
}: StoreProfileScreenProps) {
  const [showLoyalty, setShowLoyalty] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const {
    showToast
  } = useToast();
  const handleCopyId = () => {
    navigator.clipboard.writeText(store.storeId);
    showToast('Store ID copied!', 'success');
  };
  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
    showToast(isFavorite ? 'Removed from favorites' : 'Added to favorites', 'info');
  };
  return <div className="pb-24 bg-white min-h-screen animate-in slide-in-from-right duration-300" data-id="element-644">
      {/* Hero Image */}
      <div className="relative h-64" data-id="element-645">
        <img src={store.image} alt={store.name} className="w-full h-full object-cover" data-id="element-646" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-647" />

        <button onClick={onBack} className="absolute top-6 left-4 p-2 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/30 transition-colors" data-id="element-648">
          <ArrowLeft size={20} data-id="element-649" />
        </button>

        <div className="absolute bottom-0 left-0 right-0 p-6 text-white" data-id="element-650">
          <div className="flex justify-between items-end mb-2" data-id="element-651">
            <h1 className="text-3xl font-bold leading-tight" data-id="element-652">{store.name}</h1>
            <div className="flex items-center gap-1 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-lg font-bold text-sm" data-id="element-653">
              <Star size={14} fill="currentColor" data-id="element-654" />
              {store.rating}
            </div>
          </div>
          <p className="text-white/80 flex items-center gap-2 text-sm" data-id="element-655">
            <MapPin size={14} data-id="element-656" /> {store.address} • {store.distance}
          </p>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6" data-id="element-657">
        {/* Actions Row */}
        <div className="flex items-center justify-between gap-4" data-id="element-658">
          <div className="flex-1 bg-gray-50 rounded-xl p-3 flex items-center justify-between border border-gray-100" data-id="element-659">
            <div data-id="element-660">
              <p className="text-xs text-gray-500 uppercase font-bold" data-id="element-661">
                Store ID
              </p>
              <p className="font-mono font-bold text-gray-900" data-id="element-662">
                {store.storeId}
              </p>
            </div>
            <button onClick={handleCopyId} className="p-2 text-gray-400 hover:text-purple-600" data-id="element-663">
              <Copy size={18} data-id="element-664" />
            </button>
          </div>
          <button onClick={toggleFavorite} className={`p-4 rounded-xl border transition-colors ${isFavorite ? 'bg-red-50 border-red-100 text-red-500' : 'bg-gray-50 border-gray-100 text-gray-400'}`} data-id="element-665">
            <Heart size={24} fill={isFavorite ? 'currentColor' : 'none'} data-id="element-666" />
          </button>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-4" data-id="element-667">
          <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50" data-id="element-668">
            <Clock size={20} className="text-purple-600 mt-0.5" data-id="element-669" />
            <div data-id="element-670">
              <p className="text-xs font-bold text-gray-900" data-id="element-671">Open Today</p>
              <p className="text-xs text-gray-500" data-id="element-672">{store.hours}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50" data-id="element-673">
            <Phone size={20} className="text-purple-600 mt-0.5" data-id="element-674" />
            <div data-id="element-675">
              <p className="text-xs font-bold text-gray-900" data-id="element-676">Contact</p>
              <p className="text-xs text-gray-500" data-id="element-677">{store.phone}</p>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2" data-id="element-678">
          {store.tags.map(tag => <span key={tag} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-semibold" data-id="element-679">
              {tag}
            </span>)}
        </div>

        {/* Reward Card */}
        <div onClick={() => setShowLoyalty(true)} className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-5 text-white shadow-lg cursor-pointer active:scale-[0.98] transition-transform" data-id="element-680">
          <div className="flex justify-between items-start mb-4" data-id="element-681">
            <div data-id="element-682">
              <p className="text-purple-200 text-xs font-bold uppercase tracking-wider mb-1" data-id="element-683">
                Loyalty Status
              </p>
              <h3 className="text-xl font-bold" data-id="element-684">7 / 10 Stamps</h3>
            </div>
            <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm" data-id="element-685">
              <Award size={24} className="text-yellow-300" data-id="element-686" />
            </div>
          </div>
          <div className="w-full bg-black/20 rounded-full h-2 mb-2" data-id="element-687">
            <div className="bg-[#FFFC00] h-2 rounded-full" style={{
            width: '70%'
          }} data-id="element-688" />
          </div>
          <p className="text-xs text-purple-100" data-id="element-689">
            3 more stamps until your free reward!
          </p>
        </div>

        {/* Main Actions */}
        <div className="space-y-3" data-id="element-690">
          <Button fullWidth size="lg" onClick={onViewMenu} data-id="element-691">
            <Utensils className="mr-2" size={18} data-id="element-692" /> View Menu & Order
          </Button>
          <Button fullWidth variant="accent" size="lg" onClick={onCheckIn} data-id="element-693">
            <MapPin className="mr-2" size={18} data-id="element-694" /> Check In Here
          </Button>
          <Button fullWidth variant="secondary" onClick={() => {}} data-id="element-695">
            <Gift className="mr-2" size={18} data-id="element-696" /> Send Gift Card
          </Button>
        </div>
      </div>

      {showLoyalty && <LoyaltyCard stamps={7} storeName={store.name} onClose={() => setShowLoyalty(false)} data-id="element-697" />}
    </div>;
}